import React, { useEffect, useState } from "react";

// Modules
import { ThemeProvider } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { useNavigate } from "react-router-dom";

// App
import {
  pageTitle,
  permissionsEnabled,
  themesEnabled,
  favoritesEnabled,
  appConfigEnabled,
  client,
  counterEnabled,
  deepLinks,
  Sidebar,
  pushNotificationsEnabled,
  notificationsEnabled,
  serverUrl,
} from "./config";
import Routes from "./core/routes";
import { getOnlineNode } from "../src/core/getNode";
import Header from "./partials/header";
import LoadingSpinner from "./partials/loadingSpinner";
import ScrollToTop from "./partials/scrollToTop";
import ClientTheme from "./core/clientTheme";
import { useWindowSize } from "./partials/windowSize";
import { AuthProvider } from "./contexts/authContext";
import { SlideConfigProvider } from "./contexts/slideConfigContext";
import LoggedOutBanner from "./partials/loggedOut";
import { postNode } from "./core/postNode";

// UI components
import PushNotificationsDialog, {
  NotificationsHandler,
  getSavedFcmToken,
} from "./partials/pushNotificationsDialog";
import Container from "react-bootstrap/Container";

// Style
import { companyappTheme } from "./core/theme";
import "./styles/main.scss";

const link = document.createElement("link");
link.rel = "stylesheet";
link.href = `https://app-download.companyapp.net/presentation-styles.php?site=${window.location.host}`;
document.head.appendChild(link);

export default function App() {
  const [isLoading, setLoading] = useState(true);
  const [isThemesLoading, setThemesLoading] = useState(true);
  const [isMobile, setMobile] = useState(
    window.innerWidth < 993 ? true : false
  );

  const [openDialog, setOpenDialog] = useState(true);
  const [isFluid, setFluid] = useState(false);
  const [navbarState, setNavbarState] = useState("nav-collapsed");

  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");

  // hooks
  // const location = useLocation();
  const showNavigation = useSelector((state) => state).navigationReducer
    .showNavigation;
  const manualToggle = useSelector((state) => state).navigationReducer
    .manualToggle;
  const user = useSelector((state) => state).authReducer.user;
  const currentLocation = useSelector(
    (state) => state.locationReducer.currentLocation
  );
  const { width } = useWindowSize();

  const isPresentationView =
    currentLocation === "preview-presentations" ||
    currentLocation === "view-presentations"
      ? true
      : false;

  // hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const checkSession = (_user) => {
    getOnlineNode("user/" + _user.current_user.uid)
      .then(() => {
        dispatch({ type: "SAVE_SESSION", payload: _user });
        checkPermissions(_user);
      })
      .catch((_error) => {
        dispatch({ type: "REMOVE_SESSION", payload: {} });
        setLoading(false);
      });
  };

  const checkPermissions = (_user) => {
    if (_user.current_user) {
      if (permissionsEnabled) {
        getOnlineNode(`api/user_permissions`)
          .then((response) => {
            if (response.data) {
              dispatch({ type: "SAVE_PERMISSIONS", payload: response.data });
              setLoading(false);
            }
          })
          .catch((error) => {
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const sidebarVisibility = () => {
    dispatch({
      type: "TOGGLE_NAVIGATION",
      payload: {
        showNavigation: !showNavigation,
        manualToggle: true,
      },
    });
  };

  const handleClicks = (event) => {
    const linkElement = event.target.closest("a");

    // If there's no <a> element in the hierarchy, exit
    if (linkElement && !linkElement.classList.contains("cdx-popup-link") && !linkElement.classList.contains("cdx-slide-link")) {
      const link = linkElement.href;
      const isDeepLink = deepLinks.some((scheme) => link.indexOf(scheme) === 0);

      if (isDeepLink) {
        const schemeRemoved = link.replace(/^[a-z]+:\/\//i, "");
        const parts = schemeRemoved.split("/");
        const newLink = `${window.location.origin}/${parts[0]}/${parts[2]}`;
        window.location.href = newLink;
        return false;
      } else {
        const isExternal = link.indexOf(window.location.origin) === -1;

        

        if (isExternal) {
          event.preventDefault();
          event.stopPropagation();

          window.open(link, "_blank");
          return false;
        }
      }
    }
  };

  // set page title
  useEffect(() => {
    document.title = pageTitle;

    const basePath = `${window.location.origin}/${client}`;
    const timestamp = Date.now();

    const faviconPath = `${basePath}/favicon.ico?v=${timestamp}`;
    let faviconTag = document.querySelector("link[rel~='icon']");
    if (!faviconTag) {
      faviconTag = document.createElement("link");
      faviconTag.rel = "icon";
      faviconTag.type = "image/x-icon";
      document.head.appendChild(faviconTag);
    }
    faviconTag.href = faviconPath;

    const manifestPath = `${basePath}/manifest.json?v=${timestamp}`;
    let manifestTag = document.querySelector("link[rel='manifest']");
    if (!manifestTag) {
      manifestTag = document.createElement("link");
      manifestTag.rel = "manifest";
      document.head.appendChild(manifestTag);
    }
    manifestTag.href = manifestPath;
  }, []);

  // Input listener for editorjs to mark empty blocks
  useEffect(() => {
    const updateEmptyState = (el) => {
      const children = Array.from(el.childNodes).filter(
        (node) =>
          !(node.nodeType === Node.ELEMENT_NODE && node.tagName === "BR") &&
          !(node.nodeType === Node.TEXT_NODE && node.textContent.trim() === "")
      );
      const isEmpty = children.length === 0;
      el.setAttribute("data-empty", isEmpty ? "true" : "false");
    };

    const observeEditable = (el) => {
      if (!el.__tracked) {
        updateEmptyState(el);
        el.addEventListener("input", handleInput);
        el.__tracked = true; // flag to prevent double binding
      }
    };

    const handleInput = (e) => {
      updateEmptyState(e.currentTarget);
    };

    const processAll = () => {
      document
        .querySelectorAll('.codex-editor [contenteditable="true"]')
        .forEach(observeEditable);
    };

    // Initial run
    processAll();

    // MutationObserver for added/removed nodes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches?.('.codex-editor [contenteditable="true"]'))
              observeEditable(node);
            node
              .querySelectorAll?.('.codex-editor [contenteditable="true"]')
              .forEach(observeEditable);
          }
        });
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.__tracked) {
            node.removeEventListener("input", handleInput);
            delete node.__tracked;
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      document
        .querySelectorAll('.codex-editor [contenteditable="true"]')
        .forEach((el) => {
          el.removeEventListener("input", handleInput);
          delete el.__tracked;
        });
      observer.disconnect();
    };
  }, []);

  // click listener to handle deeplinks for the web
  useEffect(() => {
    window.addEventListener("click", handleClicks, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      window.removeEventListener("click", handleClicks);
    };
  }, []);

  // resize effect
  useEffect(() => {
    const handleResize = () => {
      if (
        currentLocation === "presentations-creator" ||
        currentLocation === "edit-presentations"
      ) {
        return;
      }

      const { innerWidth: width } = window;

      if (!manualToggle) {
        if (width < 993) {
          dispatch({
            type: "TOGGLE_NAVIGATION",
            payload: {
              showNavigation: false,
            },
          });

          setMobile(true);
        } else {
          dispatch({
            type: "TOGGLE_NAVIGATION",
            payload: {
              showNavigation: true,
            },
          });

          setMobile(false);
        }
      }
    };

    window.addEventListener("resize", handleResize, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, [currentLocation, manualToggle]);

  // store client theme to redux
  useEffect(() => {
    dispatch({ type: "SET_CLIENT_THEME", payload: { ...ClientTheme() } });
    // eslint-disable-next-line
  }, []);

  // check user
  useEffect(() => {
    let session = localStorage.getItem("user");
    session = JSON.parse(session);
    session = {
      user: session,
    };

    if (session && session.user && session.user.current_user) {
      checkSession(session.user);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // get config
  useEffect(() => {
    if (appConfigEnabled) {
      getOnlineNode("api/config")
        .then((response) => {
          dispatch({ type: "UPDATE_CONTENT", payload: response.data });
        })
        .catch((error) => {
          console.log("err: ", error);
        });
    }
    // eslint-disable-next-line
  }, []);

  // Lister to user redux
  // Get permissions
  // Get counter
  useEffect(() => {
    if (user.current_user) {
      if (permissionsEnabled) {
        getOnlineNode(`api/user_permissions`)
          .then((response) => {
            if (response.data) {
              dispatch({ type: "SAVE_PERMISSIONS", payload: response.data });
              setLoading(false);
            }
          })
          .catch((error) => {
            setLoading(false);
          });
      }

      if (counterEnabled) {
        getOnlineNode("api/v2/counter?_format=json")
          .then((response) => {
            dispatch({ type: "UPDATE_COUNTER", payload: response.data });
          })
          .catch((error) => {});
      }

      if (pushNotificationsEnabled) {
        storeToken();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.current_user]);

  // get favourites on user and location change
  useEffect(() => {
    if (user.current_user) {
      getFavorites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.current_user, currentLocation]);

  // Check for notifications
  useEffect(() => {
    if (user.current_user) {
      if (notificationsEnabled) {
        getOnlineNode(`api/notifications?_format=json`)
          .then((response) => {
            dispatch({ type: "SAVE_NOTIFICATIONS", payload: response.data });
          })
          .catch((error) => {
            console.log("notifications error", error);
          });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.current_user]);

  // Re-gen csrf_token
  useEffect(() => {
    if (user.current_user) {
      getOnlineNode(`session/token?_format=json`)
        .then((response) => {
          let newUser = user;
          newUser.csrf_token = response.data;

          dispatch({ type: "SAVE_SESSION", payload: newUser });
        })
        .catch((error) => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    let _navState = "nav-collapsed";
    if (showNavigation || showNavigation === "true") {
      _navState = "nav-expanded";
    }

    setNavbarState(_navState);
  }, [showNavigation]);

  useEffect(() => {
    let _fluid = false;

    if (
      currentLocation === "presentations-creator" ||
      currentLocation === "edit-presentations" ||
      currentLocation === "view-presentations" ||
      currentLocation === "preview-presentations" ||
      currentLocation === "global-portfolio" ||
      currentLocation.includes("-creator") ||
      window.location.href.indexOf("push-notifications") > -1
    ) {
      _fluid = true;
    }

    setFluid(_fluid);
  }, [currentLocation]);

  // get themes
  useEffect(() => {
    if (themesEnabled) {
      getOnlineNode("api/themes")
        .then((response) => {
          dispatch({ type: "SAVE_THEMES", payload: response.data });
          setThemesLoading(false);
        })
        .catch((error) => {
          setThemesLoading(false);
        });
    } else {
      setThemesLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  const storeToken = async () => {
    const fcmToken = getSavedFcmToken();

    if (fcmToken) {
      const payload = {
        token: fcmToken,
        uid: user?.current_user?.uid ? user?.current_user?.uid : null,
        topics: ["all"],
        device: `Web`,
      };

      postNode(`${serverUrl}/api/fcm/token`, payload, user.csrf_token)
        .then((response) => {
          // console.log(response);
        })
        .catch((error) => {
          console.log("Failed to store fcm token", error);
        });
    }
  };

  const getFavorites = () => {
    if (
      favoritesEnabled &&
      user &&
      user.current_user &&
      user.current_user.uid
    ) {
      const path =
        client === "equans"
          ? `api/favorites/${user.current_user.uid}?_format=json`
          : `api/favorites?_format=json`;

      getOnlineNode(path)
        .then((response) => {
          if (response.data) {
            dispatch({ type: "UPDATE_FAVORITES", payload: response.data });
          }
        })
        .catch((error) => {
          setLoading(false);
        });
    }
  };

  if (isLoading || isThemesLoading) {
    return <LoadingSpinner className="spinner-container--app" />;
  }

  let theme = companyappTheme();

  return (
    <ThemeProvider theme={theme}>
      <SlideConfigProvider>
      <SnackbarProvider maxSnack={5}>
        <NotificationsHandler />

        <ScrollToTop />
        {isPresentationView ? (
          <>
            <div className={`page-container`} data-page={currentLocation}>
              <Container fluid={true} data-page={currentLocation}>
                <Routes />
              </Container>
            </div>
          </>
        ) : (
          <AuthProvider>
            <Header
              showNavigation={showNavigation}
              sidebarVisibility={sidebarVisibility}
              navbarState={navbarState}
              hash={hash}
            />
            {hash === null && (
              <Sidebar
                showNavigation={showNavigation}
                isMobile={isMobile}
                sidebarVisibility={sidebarVisibility}
              />
            )}
            <div
              className={`page-container ${hash === null ? navbarState : ""}`}
              data-page={currentLocation}
            >
              <Container
                fluid={isFluid}
                className={`${isFluid ? "" : "content-container"} ${
                  width < 576 ? "px-4" : ""
                }`}
                data-page={currentLocation}
              >
                <LoggedOutBanner />
                <Routes />
              </Container>
            </div>

            {pushNotificationsEnabled &&
              typeof window !== "undefined" &&
              "serviceWorker" in navigator &&
              "PushManager" in window &&
              "Notification" in window && (
                <PushNotificationsDialog
                  open={openDialog}
                  onClose={() => setOpenDialog(false)}
                  user={user}
                  navigate={navigate}
                />
              )}
          </AuthProvider>
        )}
      </SnackbarProvider>
      </SlideConfigProvider>
    </ThemeProvider>
  );
}
